import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  AUTH_FIELD_CLASSES,
  AUTH_LABEL_CLASSES,
  AUTH_PRIMARY_ACTION_CLASSES,
  AUTH_SECONDARY_ACTION_CLASSES,
  AuthPageHeader,
  AuthPageLayout,
} from "@/components/auth/AuthPageLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { Loader2, AlertCircle, CheckCircle, Eye, EyeOff } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [haveResetToken, setHaveResetToken] = useState<boolean | null>(null);
  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    // Listen for PASSWORD_RECOVERY event (recommended Supabase approach)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        logger.debug("auth", "password-recovery-event", "PASSWORD_RECOVERY event received");
        setHaveResetToken(true);
        setTokenInvalid(false);
        setError(null);
      }
    });

    // Also check current session on mount (handles page refresh after clicking link)
    const checkSession = async () => {
      try {
        // Check if we have hash params indicating a recovery flow
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const type = hashParams.get("type");
        const accessToken = hashParams.get("access_token");

        if (type === "recovery" && accessToken) {
          logger.debug("auth", "reset-token-present", "Reset token found in URL hash");
          // Supabase client will automatically handle the token exchange
          // Just wait a moment for the auth state to update
          timeoutId = setTimeout(async () => {
            const {
              data: { session },
            } = await supabase.auth.getSession();
            if (session) {
              setHaveResetToken(true);
            } else {
              setHaveResetToken(false);
              setTokenInvalid(true);
              setError(t.forms.passwordReset.invalidToken);
            }
          }, 500);
        } else {
          // Check if already in a valid session (e.g., from PASSWORD_RECOVERY event)
          const {
            data: { session },
          } = await supabase.auth.getSession();
          if (session) {
            setHaveResetToken(true);
          } else {
            setHaveResetToken(false);
            setTokenInvalid(true);
            setError(t.forms.passwordReset.invalidToken);
          }
        }
      } catch (err) {
        logger.error("auth", "token-check-error", "Error checking reset token", err);
        setHaveResetToken(false);
        setTokenInvalid(true);
        setError(t.forms.passwordReset.invalidToken);
      }
    };

    checkSession();

    return () => {
      subscription.unsubscribe();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [t.forms.passwordReset.invalidToken]);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error(t.forms.passwordReset.passwordMismatch);
      return;
    }

    if (password !== confirmPassword) {
      toast.error(t.forms.passwordReset.passwordMismatch);
      return;
    }

    if (password.length < 8) {
      toast.error(t.forms.passwordReset.passwordTooShort);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      logger.info("auth", "password-reset-attempt", "User attempting to set new password");

      const { error } = await supabase.auth.updateUser({ password });

      if (error) {
        logger.warn("auth", "password-reset-failed", "Password reset failed", {
          error: error.message,
        });
        setError(error.message);
        toast.error(error.message || t.forms.error.unexpectedError);
      } else {
        logger.info("auth", "password-reset-success", "Password reset successful");
        setResetSuccess(true);
        toast.success(t.forms.passwordReset.resetSuccess);

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (error) {
      logger.error(
        "auth",
        "password-reset-error",
        "Unexpected error during password reset",
        error as Error
      );
      setError(t.forms.error.unexpectedError);
      toast.error(t.forms.error.unexpectedError);
    } finally {
      setLoading(false);
    }
  };

  if (haveResetToken === null) {
    return (
      <AuthPageLayout>
        <AuthPageHeader
          title={t.forms.passwordReset.verifying}
          description={t.forms.passwordReset.description}
          icon={<Loader2 className="h-6 w-6 animate-spin" />}
        />
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout>
      <AuthPageHeader
        title={resetSuccess ? t.forms.passwordReset.checkEmailTitle : t.forms.passwordReset.title}
        description={
          resetSuccess
            ? t.forms.passwordReset.resetSuccess
            : tokenInvalid
              ? t.forms.passwordReset.invalidToken
              : t.forms.passwordReset.description
        }
        icon={
          resetSuccess ? (
            <CheckCircle className="h-6 w-6" />
          ) : tokenInvalid ? (
            <AlertCircle className="h-6 w-6" />
          ) : undefined
        }
        iconClassName={
          resetSuccess
            ? "bg-green-100 text-green-600"
            : tokenInvalid
              ? "bg-destructive/10 text-destructive"
              : undefined
        }
      />

      <div className="space-y-5">
        {error && (
          <Alert variant="destructive" className="rounded-xl">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t.common.error}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {resetSuccess ? (
          <Alert variant="default" className="rounded-xl border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-800">{t.common.success}</AlertTitle>
            <AlertDescription className="text-green-700">
              {t.forms.passwordReset.resetSuccess}
            </AlertDescription>
          </Alert>
        ) : haveResetToken ? (
          <form onSubmit={handleResetPassword} className="space-y-[18px]">
            <div className="space-y-2">
              <Label htmlFor="password" className={AUTH_LABEL_CLASSES}>
                {t.forms.passwordReset.newPasswordLabel}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t.forms.passwordReset.newPasswordLabel}
                  disabled={loading}
                  required
                  className={cn(AUTH_FIELD_CLASSES, "pr-12")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg text-neutral-500 hover:text-foreground"
                  aria-label={showPassword ? t.forms.hidePassword : t.forms.showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className={AUTH_LABEL_CLASSES}>
                {t.forms.passwordReset.confirmPasswordLabel}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t.forms.passwordReset.confirmPasswordLabel}
                  disabled={loading}
                  required
                  className={cn(AUTH_FIELD_CLASSES, "pr-12")}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg text-neutral-500 hover:text-foreground"
                  aria-label={showPassword ? t.forms.hidePassword : t.forms.showPassword}
                  onClick={() => setShowPassword((prev) => !prev)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button type="submit" className={AUTH_PRIMARY_ACTION_CLASSES} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t.forms.status.saving}
                </>
              ) : (
                t.forms.passwordReset.resetPasswordButton
              )}
            </Button>
          </form>
        ) : (
          <Button
            onClick={() => navigate("/forgot-password")}
            variant="outline"
            className={AUTH_SECONDARY_ACTION_CLASSES}
          >
            {t.forms.passwordReset.requestNewResetLinkButton}
          </Button>
        )}

        <Button
          variant="ghost"
          onClick={() => navigate("/login")}
          className="h-11 w-full rounded-xl text-[14px] font-semibold"
        >
          {t.forms.passwordReset.backToLogin}
        </Button>
      </div>
    </AuthPageLayout>
  );
}
